import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  BookOpen, 
  Users, 
  Search, 
  MessageCircle,
  ChevronDown,
  ChevronRight,
  BookMarked,
  FileText,
  GraduationCap,
  Scale,
  Briefcase,
  Heart,
  Globe,
  Cpu,
  Leaf,
  Building,
  TrendingUp
} from "lucide-react";

export default function PreWrittenContent() {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const whatsappLink = "https://wa.me/916284607500";

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  return (
    <div data-testid="pre-written-content-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img 
            src="https://images.unsplash.com/photo-1521587760476-6c12a4b040da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxsaWJyYXJ5JTIwYm9va3MlMjBhY2FkZW1pY3xlbnwwfHx8fDE3ODExMTE1MDh8MA&ixlib=rb-4.1.0&q=85" 
            alt="Library Books"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="h-[1px] w-12 bg-[#C5A059]" />
              <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Academic Repository</span>
              <div className="h-[1px] w-12 bg-[#C5A059]" />
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl font-medium text-white mb-6">
              Pre-Written <span className="text-[#C5A059]">Contents</span>
            </h1>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
              Explore our comprehensive repository of ready-to-publish academic books and edited volumes across multiple disciplines.
            </p>
            
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 mb-10">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#C5A059]">100+</div>
                <div className="text-slate-400 text-sm">Authored Books</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#C5A059]">50+</div>
                <div className="text-slate-400 text-sm">Edited Volumes</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#C5A059]">10</div>
                <div className="text-slate-400 text-sm">Subject Areas</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image Section */}
      <section className="py-12 bg-[#C5A059]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 items-center">
            <div className="text-center md:text-left">
              <BookMarked className="w-12 h-12 text-slate-950 mx-auto md:mx-0 mb-4" />
              <h3 className="font-serif text-2xl font-medium text-slate-950 mb-2">Quality Assured</h3>
              <p className="text-slate-800">All contents are written by subject matter experts</p>
            </div>
            <div className="text-center">
              <FileText className="w-12 h-12 text-slate-950 mx-auto mb-4" />
              <h3 className="font-serif text-2xl font-medium text-slate-950 mb-2">ISBN Ready</h3>
              <p className="text-slate-800">Ready for publication with ISBN assignment</p>
            </div>
            <div className="text-center md:text-right">
              <GraduationCap className="w-12 h-12 text-slate-950 mx-auto md:ml-auto md:mr-0 mb-4" />
              <h3 className="font-serif text-2xl font-medium text-slate-950 mb-2">Academic Standard</h3>
              <p className="text-slate-800">Meets highest scholarly standards</p>
            </div>
          </div>
        </div>
      </section>

      {/* Book Repository */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-medium text-slate-900 mb-6">Subject-Wise Repository</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto mb-6" />
            <p className="text-slate-600 max-w-2xl mx-auto">
              Click on any subject to view available authored and edited book titles
            </p>
          </div>

          <div className="space-y-4">
            {/* Law & Legal Studies */}
            <CategoryCard 
              id="law"
              name="Law & Legal Studies"
              icon={Scale}
              color="from-blue-600 to-blue-800"
              authoredCount={10}
              editedCount={6}
              isExpanded={expandedCategory === "law"}
              onToggle={() => toggleCategory("law")}
              whatsappLink={whatsappLink}
              authoredBooks={["Constitutional Law: Principles and Practice", "Criminal Law and Procedure in India", "Contract Law: A Comprehensive Guide", "Property Law and Land Reforms", "Administrative Law: Theory and Application", "Environmental Law and Sustainable Development", "International Trade Law", "Human Rights Law: Global Perspectives", "Family Law and Personal Laws in India", "Intellectual Property Rights: Protection & Enforcement"]}
              editedBooks={["Contemporary Issues in Constitutional Law (Edited Volume)", "Emerging Trends in Criminal Justice (Edited)", "Legal Education in the 21st Century (Edited)", "Women and Law: A Multi-Disciplinary Approach (Edited)", "Cyber Law and Digital Privacy (Edited Volume)", "Corporate Governance and Legal Compliance (Edited)"]}
            />

            {/* Management & Business */}
            <CategoryCard 
              id="management"
              name="Management & Business"
              icon={Briefcase}
              color="from-emerald-600 to-emerald-800"
              authoredCount={10}
              editedCount={5}
              isExpanded={expandedCategory === "management"}
              onToggle={() => toggleCategory("management")}
              whatsappLink={whatsappLink}
              authoredBooks={["Principles of Management: Modern Approaches", "Strategic Management and Business Policy", "Human Resource Management: Theory & Practice", "Marketing Management in Digital Era", "Financial Management: Concepts and Applications", "Operations Management and Supply Chain", "Organizational Behavior and Leadership", "Entrepreneurship and New Venture Creation", "Business Ethics and Corporate Responsibility", "Project Management: Tools and Techniques"]}
              editedBooks={["Innovation in Business Management (Edited Volume)", "Sustainable Business Practices (Edited)", "Digital Transformation in Organizations (Edited)", "Leadership in Times of Crisis (Edited Volume)", "Women in Management: Breaking Barriers (Edited)"]}
            />

            {/* Commerce & Economics */}
            <CategoryCard 
              id="commerce"
              name="Commerce & Economics"
              icon={TrendingUp}
              color="from-purple-600 to-purple-800"
              authoredCount={10}
              editedCount={4}
              isExpanded={expandedCategory === "commerce"}
              onToggle={() => toggleCategory("commerce")}
              whatsappLink={whatsappLink}
              authoredBooks={["Principles of Economics: Micro and Macro", "Indian Economy: Growth and Development", "International Economics and Trade", "Financial Accounting: A Practical Approach", "Cost and Management Accounting", "Banking and Financial Services", "Taxation Law and Practice", "Auditing and Assurance Standards", "Corporate Finance and Investment", "E-Commerce and Digital Business"]}
              editedBooks={["Economic Reforms and Policy Changes (Edited)", "Financial Inclusion in India (Edited Volume)", "GST Implementation: Challenges & Opportunities (Edited)", "Sustainable Economic Development (Edited)"]}
            />

            {/* Political Science */}
            <CategoryCard 
              id="political-science"
              name="Political Science"
              icon={Building}
              color="from-red-600 to-red-800"
              authoredCount={10}
              editedCount={4}
              isExpanded={expandedCategory === "political-science"}
              onToggle={() => toggleCategory("political-science")}
              whatsappLink={whatsappLink}
              authoredBooks={["Indian Political System: Structure and Functions", "Comparative Politics: Global Perspectives", "International Relations: Theory and Practice", "Public Administration: Principles and Practice", "Indian Foreign Policy: Evolution and Challenges", "Political Thought: Western and Indian", "Governance and Public Policy", "Electoral Politics in India", "Democracy and Development", "Federalism in India: Issues and Challenges"]}
              editedBooks={["Contemporary Issues in Indian Politics (Edited)", "Global Governance in the 21st Century (Edited)", "Women in Politics: A Global Perspective (Edited)", "Diplomacy and International Relations (Edited Volume)"]}
            />

            {/* Education */}
            <CategoryCard 
              id="education"
              name="Education"
              icon={GraduationCap}
              color="from-orange-600 to-orange-800"
              authoredCount={10}
              editedCount={4}
              isExpanded={expandedCategory === "education"}
              onToggle={() => toggleCategory("education")}
              whatsappLink={whatsappLink}
              authoredBooks={["Foundations of Education: Philosophy and Practice", "Educational Psychology: Learning and Development", "Curriculum Development and Design", "Teaching Methods and Strategies", "Educational Technology and E-Learning", "Inclusive Education: Theory and Practice", "Guidance and Counseling in Schools", "Educational Research Methodology", "Teacher Education and Professional Development", "Assessment and Evaluation in Education"]}
              editedBooks={["NEP 2020: Implementation and Challenges (Edited)", "Quality Education for All (Edited Volume)", "Digital Learning: Post-Pandemic Perspectives (Edited)", "Higher Education Reforms in India (Edited)"]}
            />

            {/* Computer Science */}
            <CategoryCard 
              id="computer-science"
              name="Computer Science & IT"
              icon={Cpu}
              color="from-cyan-600 to-cyan-800"
              authoredCount={10}
              editedCount={4}
              isExpanded={expandedCategory === "computer-science"}
              onToggle={() => toggleCategory("computer-science")}
              whatsappLink={whatsappLink}
              authoredBooks={["Data Structures and Algorithms", "Database Management Systems", "Computer Networks and Security", "Artificial Intelligence: Fundamentals", "Machine Learning: Concepts and Applications", "Web Development Technologies", "Cloud Computing and Services", "Software Engineering Principles", "Cyber Security and Digital Forensics", "Internet of Things (IoT): A Practical Guide"]}
              editedBooks={["AI Applications in Various Domains (Edited Volume)", "Blockchain Technology and Applications (Edited)", "Big Data Analytics: Methods and Tools (Edited)", "Emerging Trends in Cybersecurity (Edited)"]}
            />

            {/* Sociology */}
            <CategoryCard 
              id="sociology"
              name="Sociology & Social Work"
              icon={Users}
              color="from-pink-600 to-pink-800"
              authoredCount={10}
              editedCount={4}
              isExpanded={expandedCategory === "sociology"}
              onToggle={() => toggleCategory("sociology")}
              whatsappLink={whatsappLink}
              authoredBooks={["Introduction to Sociology: Indian Perspective", "Social Problems in Contemporary India", "Rural and Urban Sociology", "Social Stratification and Mobility", "Gender and Society", "Social Work: Methods and Practice", "Community Development and Organization", "Child and Family Welfare", "Social Research Methods", "Sociology of Development"]}
              editedBooks={["Women Empowerment in India (Edited Volume)", "Social Exclusion and Marginalization (Edited)", "Youth and Society: Contemporary Issues (Edited)", "Social Work in Healthcare Settings (Edited)"]}
            />

            {/* Environmental Studies */}
            <CategoryCard 
              id="environmental"
              name="Environmental Studies"
              icon={Leaf}
              color="from-green-600 to-green-800"
              authoredCount={10}
              editedCount={4}
              isExpanded={expandedCategory === "environmental"}
              onToggle={() => toggleCategory("environmental")}
              whatsappLink={whatsappLink}
              authoredBooks={["Environmental Science: Fundamentals", "Ecology and Biodiversity Conservation", "Climate Change: Science and Policy", "Sustainable Development Goals", "Environmental Impact Assessment", "Natural Resource Management", "Pollution Control and Management", "Environmental Law and Policy", "Green Technology and Innovation", "Environmental Economics"]}
              editedBooks={["Climate Action and Global Cooperation (Edited)", "Sustainable Cities and Communities (Edited Volume)", "Renewable Energy: Prospects and Challenges (Edited)", "Water Resource Management (Edited)"]}
            />

            {/* Medical Sciences */}
            <CategoryCard 
              id="medical"
              name="Medical & Health Sciences"
              icon={Heart}
              color="from-rose-600 to-rose-800"
              authoredCount={10}
              editedCount={4}
              isExpanded={expandedCategory === "medical"}
              onToggle={() => toggleCategory("medical")}
              whatsappLink={whatsappLink}
              authoredBooks={["Public Health: Principles and Practice", "Community Medicine and Health", "Nursing: Theory and Practice", "Hospital Administration and Management", "Mental Health and Psychiatry", "Pharmacology: A Clinical Approach", "Nutrition and Dietetics", "Health Education and Promotion", "Epidemiology: Methods and Applications", "Medical Ethics and Law"]}
              editedBooks={["Healthcare Systems in India (Edited Volume)", "Mental Health: A Multi-Disciplinary Approach (Edited)", "Pandemic Preparedness and Response (Edited)", "Women's Health Issues (Edited)"]}
            />

            {/* International Studies */}
            <CategoryCard 
              id="international"
              name="International Studies"
              icon={Globe}
              color="from-indigo-600 to-indigo-800"
              authoredCount={10}
              editedCount={4}
              isExpanded={expandedCategory === "international"}
              onToggle={() => toggleCategory("international")}
              whatsappLink={whatsappLink}
              authoredBooks={["International Relations: Theory and Practice", "Global Politics and World Order", "Diplomacy: Art and Practice", "International Organizations and Global Governance", "Foreign Policy Analysis", "International Trade and Economics", "Security Studies: Traditional and Non-Traditional", "Human Rights in Global Context", "Area Studies: South Asia, Middle East, Europe", "International Law: Principles and Cases"]}
              editedBooks={["India's Role in Global Affairs (Edited Volume)", "Regional Cooperation in South Asia (Edited)", "International Security: Contemporary Issues (Edited)", "Global Migration and Refugee Studies (Edited)"]}
            />
          </div>
        </div>
      </section>

      {/* Visual Gallery */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-medium text-slate-900 mb-6">Our Collection</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="relative h-80 overflow-hidden group">
              <img 
                src="https://images.unsplash.com/photo-1521587760476-6c12a4b040da?crop=entropy&cs=srgb&fm=jpg&w=800" 
                alt="Library Collection"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-slate-950/60 flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="text-5xl font-bold mb-2">100+</div>
                  <div className="text-lg">Authored Books</div>
                </div>
              </div>
            </div>
            <div className="relative h-80 overflow-hidden group">
              <img 
                src="https://images.unsplash.com/photo-1532012197267-da84d127e765?crop=entropy&cs=srgb&fm=jpg&w=800" 
                alt="Open Book"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-[#C5A059]/80 flex items-center justify-center">
                <div className="text-center text-slate-950">
                  <div className="text-5xl font-bold mb-2">50+</div>
                  <div className="text-lg">Edited Volumes</div>
                </div>
              </div>
            </div>
            <div className="relative h-80 overflow-hidden group">
              <img 
                src="https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?crop=entropy&cs=srgb&fm=jpg&w=800" 
                alt="Academic Writing"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-slate-950/60 flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="text-5xl font-bold mb-2">10</div>
                  <div className="text-lg">Subject Areas</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-medium text-white mb-6">How to Purchase</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto" />
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C5A059] flex items-center justify-center mx-auto mb-4">
                <span className="text-slate-950 font-bold text-2xl">1</span>
              </div>
              <h3 className="font-serif text-lg font-medium text-white mb-2">Browse Repository</h3>
              <p className="text-slate-400 text-sm">Explore our subject-wise collection</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C5A059] flex items-center justify-center mx-auto mb-4">
                <span className="text-slate-950 font-bold text-2xl">2</span>
              </div>
              <h3 className="font-serif text-lg font-medium text-white mb-2">Select Title</h3>
              <p className="text-slate-400 text-sm">Choose the book that fits your needs</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C5A059] flex items-center justify-center mx-auto mb-4">
                <span className="text-slate-950 font-bold text-2xl">3</span>
              </div>
              <h3 className="font-serif text-lg font-medium text-white mb-2">Contact Us</h3>
              <p className="text-slate-400 text-sm">Reach out via WhatsApp for details</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C5A059] flex items-center justify-center mx-auto mb-4">
                <span className="text-slate-950 font-bold text-2xl">4</span>
              </div>
              <h3 className="font-serif text-lg font-medium text-white mb-2">Get Your Content</h3>
              <p className="text-slate-400 text-sm">Receive ready-to-publish content</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main CTA - Purchase Content */}
      <section className="py-20 bg-gradient-to-r from-green-500 to-green-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MessageCircle className="w-16 h-16 text-white mx-auto mb-6" />
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-white mb-6">
            Ready to Purchase Content?
          </h2>
          <p className="text-green-100 text-lg mb-10 max-w-2xl mx-auto">
            Contact us on WhatsApp to discuss your requirements, get pricing details, and purchase pre-written academic content.
          </p>
          <a 
            href={whatsappLink}
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white text-green-600 hover:bg-green-50 px-10 py-5 font-bold text-xl transition-colors shadow-xl"
            data-testid="purchase-content-btn"
          >
            <MessageCircle className="w-7 h-7" /> Purchase Content on WhatsApp
          </a>
          <p className="text-green-200 text-sm mt-6">
            +91 6284607500 • Available 24/7
          </p>
        </div>
      </section>
    </div>
  );
}

function CategoryCard({ id, name, icon: Icon, color, authoredCount, editedCount, isExpanded, onToggle, whatsappLink, authoredBooks, editedBooks }) {
  return (
    <Card className="bg-white border-0 rounded-none shadow-md overflow-hidden" data-testid={`category-${id}`}>
      <button 
        onClick={onToggle}
        className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 bg-gradient-to-br ${color} flex items-center justify-center`}>
            <Icon className="w-7 h-7 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-serif text-xl font-medium text-slate-900">{name}</h3>
            <p className="text-slate-500 text-sm">
              {authoredCount} Authored • {editedCount} Edited Volumes
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronDown className="w-6 h-6 text-[#C5A059]" />
        ) : (
          <ChevronRight className="w-6 h-6 text-slate-400" />
        )}
      </button>
      
      {isExpanded && (
        <CardContent className="px-6 pb-6 pt-0 border-t border-slate-100">
          <div className="grid md:grid-cols-2 gap-8 pt-6">
            {/* Authored Books */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-[#C5A059]" />
                <h4 className="font-semibold text-slate-900">Authored Books</h4>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{authoredBooks[0]}</span></li>
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{authoredBooks[1]}</span></li>
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{authoredBooks[2]}</span></li>
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{authoredBooks[3]}</span></li>
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{authoredBooks[4]}</span></li>
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{authoredBooks[5]}</span></li>
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{authoredBooks[6]}</span></li>
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{authoredBooks[7]}</span></li>
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{authoredBooks[8]}</span></li>
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{authoredBooks[9]}</span></li>
              </ul>
            </div>
            
            {/* Edited Books */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-[#C5A059]" />
                <h4 className="font-semibold text-slate-900">Edited Volumes</h4>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{editedBooks[0]}</span></li>
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{editedBooks[1]}</span></li>
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{editedBooks[2]}</span></li>
                <li className="flex items-start gap-2 text-slate-600 text-sm"><span className="text-[#C5A059] mt-1">•</span><span>{editedBooks[3]}</span></li>
              </ul>
            </div>
          </div>
          
          {/* Category-specific CTA */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <a 
              href={`${whatsappLink}?text=Hi, I am interested in pre-written content from the ${name} category. Please share more details.`}
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 font-medium transition-colors"
            >
              <MessageCircle className="w-5 h-5" /> Inquire About {name} Books
            </a>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
